import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'
import { useSendMdmScopeCommand } from '../../../hooks/useMdm'
import { errorMessage, messagePayload } from './mobileUtils'

const ACTIONS = [
  { type: 'locate', label: 'Localizar', hint: 'Pede a posição atual de cada tablet.' },
  { type: 'sync-policy', label: 'Reaplicar política', hint: 'Cada tablet baixa e reaplica a política dele.' },
  { type: 'message', label: 'Mensagem na tela', hint: 'Mostra um aviso em cada tablet.' },
  { type: 'screenshot', label: 'Captura de tela', hint: 'Atualiza a miniatura de cada tablet.' },
  { type: 'kiosk-resume', label: 'Reativar quiosque', hint: 'Volta ao quiosque os tablets em que um técnico saiu dele. Nos outros, nada muda.' },
  { type: 'mdm-resume', label: 'Retomar MDM', hint: 'Encerra a pausa do MDM feita por um técnico no tablet. Nos outros, nada muda.' },
  { type: 'request-accessibility', label: 'Pedir acessibilidade', hint: 'Nos tablets com a acessibilidade do agente desligada, mostra a tela para ligar (agente 1.1.1+).' },
  { type: 'lock', label: 'Bloquear tela', hint: 'Bloqueia a tela de todos (quem tem senha precisa digitá-la).' },
  { type: 'reboot', label: 'Reiniciar', hint: 'Reinicia todos os tablets — quem estiver usando perde o que estava fazendo.' },
]

/**
 * Ação para TODOS os tablets de um grupo e subgrupos (`groupId` null = tenant inteiro). Quem resolve
 * os tablets é o servidor (CONTRACT.md §11) — a tela só diz o escopo.
 */
export default function ScopeActionsModal({ isOpen, onClose, groupId = null, groupName = null, deviceCount = 0, onFeedback }) {
  const send = useSendMdmScopeCommand()
  const [type, setType] = useState('locate')
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [high, setHigh] = useState(false)
  const [requireAck, setRequireAck] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen) { setType('locate'); setTitle(''); setText(''); setHigh(false); setRequireAck(false); setError(null) }
  }, [isOpen])

  const scopeLabel = groupId ? `grupo ${groupName || ''} (com subgrupos)`.trim() : 'todos os tablets do tenant'
  const action = ACTIONS.find((a) => a.type === type)
  const invalid = type === 'message' && (!title.trim() || !text.trim())

  const handleSend = async () => {
    setError(null)
    try {
      const payload = type === 'message' ? messagePayload({ title, text, high, requireAck }) : null
      const res = await send.mutateAsync({ groupId, type, payload })
      onFeedback?.({ type: 'ok', text: `"${action.label}" enviado para ${res?.devices ?? 0} tablet(s) — ${scopeLabel}.` })
      onClose?.()
    } catch (e) {
      setError(errorMessage(e, 'Falha ao enviar a ação para o grupo.'))
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Ação no ${groupId ? 'grupo' : 'tenant'}`}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Vai para <strong>{deviceCount}</strong> tablet(s): {scopeLabel}. Tablets desligados recebem quando voltarem (até 24 h).
        </p>

        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {ACTIONS.map((a) => (
            <label key={a.type} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
              <input type="radio" className="mt-0.5" checked={type === a.type} onChange={() => setType(a.type)} />
              <span>
                <span className={clsx('text-gray-900', (a.type === 'reboot' || a.type === 'lock') && 'font-medium')}>{a.label}</span>
                <span className="block text-xs text-gray-500">{a.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {type === 'message' && (
          <div className="space-y-2">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Texto"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={high} onChange={(e) => setHigh(e.target.checked)} />
              Prioridade alta
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={requireAck} onChange={(e) => setRequireAck(e.target.checked)} />
              <span>
                Pedir confirmação de leitura (Ciente)
                <span className="block text-xs text-gray-500">A hora em que cada tablet tocou em &quot;Ciente&quot; aparece nos Comandos do tablet.</span>
              </span>
            </label>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={send.isPending || invalid || deviceCount === 0}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50',
              type === 'reboot' ? 'bg-red-600 hover:bg-red-700' : 'bg-theme-primary hover:bg-theme-primary/90')}
          >
            {send.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {action.label} em {deviceCount} tablet(s)
          </button>
        </div>
      </div>
    </Modal>
  )
}
